'use client';

/**
 * Widget de tri pour les entités
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage du sélecteur de tri
 *
 * Principe DRY:
 * - Réutilise useEntitySortConfig pour la configuration centralisée
 * - Réutilise useIsMobile pour le comportement responsive
 *
 * @module EntitySortBy
 */

import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import type { EntityType } from '@/lib/config/SearchEngine/entityFields';
import { useEntitySortConfig } from '@/lib/hooks/useEntitySortConfig';
import { useIsMobile } from '@/lib/hooks/useIsMobile';

// ============================================================================
// Types
// ============================================================================

export interface EntitySortByProps {
  /**
   * Type d'entité
   */
  entityType: EntityType;
  /**
   * Nom de l'index
   */
  indexName: string;
  /**
   * Classe CSS optionnelle
   */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Sélecteur de tri pour les entités
 * Style adaptatif avec largeur dynamique selon le contenu
 *
 * @example
 * ```tsx
 * <EntitySortBy entityType="auteurs" indexName="auteurs" />
 * ```
 */
export function EntitySortBy({
  entityType,
  indexName,
  className,
}: EntitySortByProps) {
  const { currentRefinement, refine, options } = useEntitySortConfig(
    entityType,
    indexName,
  );

  const spanRef = useRef<HTMLSpanElement>(null);
  const isMobile = useIsMobile();

  // Récupérer le label de l'option sélectionnée
  const selectedLabel =
    options.find((option) => option.value === currentRefinement)?.label || '';

  // Estimation initiale basée sur la longueur du label (environ 8px par caractère + padding)
  const estimatedWidth = selectedLabel.length * 8 + 40;
  const [width, setWidth] = useState(isMobile ? '100%' : `${estimatedWidth}px`);

  useEffect(() => {
    if (spanRef.current) {
      if (isMobile) {
        setWidth('100%');
      } else {
        setWidth(`${selectedLabel.length * 8 + 40}px`);
      }
    }
  }, [selectedLabel, currentRefinement, isMobile]);

  return (
    <div className={twMerge('', className)}>
      {/* Hidden span to measure the selected option's width */}
      <span ref={spanRef} className="invisible absolute whitespace-nowrap p-2">
        {options.find((option) => option.value === currentRefinement)?.label}
      </span>

      <select
        className="caret-icon w-auto cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm"
        style={{ width }}
        onChange={(event) => {
          refine(event.target.value);
        }}
        value={currentRefinement}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
